import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import { environment } from '@environments/environment';
import { GlobalFunctions } from '@global/GlobalFunctions';
import { DataPoint, IDataPoint } from './datapoints-data.service';
import { LevelAsset } from './assets-data.service';
import { OfflineEvent } from './events-data.service';

/**
 * IVirtualSensor
 */
export interface IVirtualSensor {	

	/** VirtualSensorID */
	VirtualSensorID: number;

	/** AccountGUID */
	AccountGUID: string;

	/** OrganisationUID */
	OrganisationUID: string;

	/** InputDataPoints */
	InputDataPoints: string;

	/** Conversion */
	Conversion: string;

	/** VirtualSensorName */
	VirtualSensorName: string;

	/** AssetIDs */
	AssetIDs: string;
}

/**
 * VirtualSensor
 */
export class VirtualSensor {

	/**
	 * Default Constructor
	 * @param VirtualSensorID VirtualSensorID 
	 * @param AccountGUID AccountGUID 
	 * @param OrganisationUID OrganisationUID 
	 * @param InputDataPoints InputDataPoints 
	 * @param Conversion Conversion 
	 * @param VirtualSensorName VirtualSensorName 
	 */
	constructor(
		public VirtualSensorID: number,
		public AccountGUID: string,
		public OrganisationUID: string,
		public InputDataPoints: string,
		public Conversion: string,
		public VirtualSensorName: string,
		public AssetIDs: string,
		public DataPointIDs?: string,
		public DataPoints?: string,
		public SI?: string,

		public _dataPoints?: {value: string, datapoint: DataPoint}[],
		public _selected?: boolean
	) { }
}

/**
 * VirtualSensorsService
 */
@Injectable({
	providedIn: 'root'
})
export class VirtualSensorsService {

	/** Default Account */
	private defaultAccountSubject: BehaviorSubject<any>;

	/** Default Account for User (JSON FORMAT) */
	public defaultAccount$: Observable<string>;

	/** API Route */
	apiRoute: string = "api/{0}/organisation/{1}/virtualSensors";

	/**
	 * Default Constructor
	 * @param http Imports the 'HttpClient' Object for internet requests
	 * @param globalFunctions globalFunctions
	 */
	constructor(
		private http: HttpClient,
		private globalFunctions: GlobalFunctions
	) { }

	/**
	 * getVirtualSensor
	 * @param accountGUID accountGUID 
	 * @param organisationID organisationID 
	 * @param virtualSensorID virtualSensorID 
	 * @returns 
	 */
	getVirtualSensor$(accountGUID: string, organisationID: string, virtualSensorID?: number, assetID?: number): Observable<any> {
		let url = this.apiRoute;
		url = url.replace("{0}", accountGUID);
		url = url.replace("{1}", organisationID);

		url = `${environment.apiUrl}/${url}`;

		if (virtualSensorID) url += `?virtualSensorID=${virtualSensorID}`;
		else if (assetID) url += `?assetID=${assetID}`;

		return this.globalFunctions.fetchAPI(this.http, url, "GET", {})
	}

	/**
	 * postVirtualSensor
	 * @param accountGUID accountGUID 
	 * @param organisationID organisationID 
	 * @param virtualSensor virtualSensor 
	 * @returns 
	 */
	postVirtualSensor$(accountGUID: string, organisationID: string, virtualSensor: VirtualSensor): Observable<any> {
		const data = JSON.stringify(virtualSensor);

		let url = this.apiRoute;
		url = url.replace("{0}", accountGUID);
		url = url.replace("{1}", organisationID);

		url = `${environment.apiUrl}/${url}`;

		return this.globalFunctions.fetchAPI(this.http, url, "POST", data)
	}

	/**
	 * patchVirtualSensor
	 * @param accountGUID accountGUID 
	 * @param organisationID organisationID 
	 * @param virtualSensorID virtualSensorID 
	 * @param virtualSensor virtualSensor 
	 * @returns 
	 */
	patchVirtualSensor$(accountGUID: string, organisationID: string, virtualSensorID: number, virtualSensor: VirtualSensor): Observable<any> {
		const data = JSON.stringify(virtualSensor);

		let url = this.apiRoute;
		url = url.replace("{0}", accountGUID);
		url = url.replace("{1}", organisationID);

		url = `${environment.apiUrl}/${url}/${virtualSensorID}`;

		return this.globalFunctions.fetchAPI(this.http, url, "PATCH", data)
	}

	/**
	 * deleteVirtualSensor
	 * @param accountGUID accountGUID 
	 * @param organisationID organisationID 
	 * @param virtualSensorID virtualSensorID 
	 * @returns 
	 */
	deleteVirtualSensor$(accountGUID: string, organisationID: string, virtualSensorID: number): Observable<any> {
		const data = {};

		let url = this.apiRoute;
		url = url.replace("{0}", accountGUID);
		url = url.replace("{1}", organisationID);

		url = `${environment.apiUrl}/${url}/${virtualSensorID}`;

		return this.globalFunctions.fetchAPI(this.http, url, "DELETE", data)
	}
}
